import { useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { APP_TYPE_APP_LAUNCH } from '@rsos/constants/appLauncherTypes';
import useHasAccessToAirAmbulance from '@rsos/hooks/useHasAccessToAirAmbulance';
import useHasAccessToIntlCallback from '@rsos/hooks/useHasAccessToIntlCallback';
import useHasAccessToTowPro from '@rsos/hooks/useHasAccessToTowPro';
import {
  TOOLS_TAB,
  AIR_AMBULANCE_TOOL,
  ALI_DISCREPANCY_TOOL,
} from '@rsos/reduxDataStore/src/features/eventPane/eventPaneTabs';
import {
  openResourcePanel,
  setResourcePanelView,
  setSelectedRequest,
} from '@rsos/reduxDataStore/src/features/resourceRequests';
import { RootState } from '../../../../app/rootReducer';
import {
  trackRequestAirAmbulanceClick,
  AIR_AMBULANCE_SOURCE_BUTTON,
} from '../../../../gainsightTrackings/airAmbulanceTrackings';
import useIsFordCall from '../../../../hooks/useIsFordCall';
import { AliDiscrepancyForm } from '../../../discrepancies';
import AirAmbulanceCard from '../AirAmbulance';
import ExternalLinkCards from '../ExternalLinkCards';
import { ToolsIntlCallbackCard } from '../IntlCallback';
import TowProCard from '../ResourceRequest/TowPro/TowProCard';
import CollaboratorBanner from './CollaboratorBanner';
import styles from './ToolsTab.module.css';

/**
 * The Tool tab of the EventPane
 */
const ToolsTab = () => {
  const hasAccessToAirAmbulance = useHasAccessToAirAmbulance();
  const hasAccessToIntlCallback = useHasAccessToIntlCallback();
  const hasAccessToTowPro = useHasAccessToTowPro();
  const isFord = useIsFordCall();

  const applications = useSelector(
    (state: RootState) => state.appLauncher.applications,
  );

  const dispatch = useDispatch();
  const selectedTool = useSelector(
    (state: RootState) => state.eventPane.selectedTool,
  );

  const handleSelectAirAmbulance = () => {
    dispatch(setSelectedRequest(null));
    dispatch(openResourcePanel());
    dispatch(setResourcePanelView('air_ambulance'));
    trackRequestAirAmbulanceClick({
      sourceButton: AIR_AMBULANCE_SOURCE_BUTTON.TOOLS_TAB,
    });
  };

  const handleSelectTowPro = () => {
    dispatch(setSelectedRequest(null));
    dispatch(openResourcePanel());
    dispatch(setResourcePanelView('towpro'));
  };

  const externalLinkApplications = useMemo(
    () =>
      applications?.filter(
        app =>
          app.type === APP_TYPE_APP_LAUNCH &&
          (app.details.launch_global || app.details.launch_call),
      ) || [],
    [applications],
  );

  if (selectedTool?.tool === ALI_DISCREPANCY_TOOL) {
    return (
      <div>
        <AliDiscrepancyForm toolProperties={selectedTool?.toolProperties} />
      </div>
    );
  }

  const tools = [
    externalLinkApplications.length > 0 && (
      <ExternalLinkCards
        externalLinkApplications={externalLinkApplications}
        key="external-link-cards"
      />
    ),
    hasAccessToAirAmbulance && (
      <AirAmbulanceCard
        tab={TOOLS_TAB}
        handleClick={handleSelectAirAmbulance}
        key={AIR_AMBULANCE_TOOL}
      />
    ),
    hasAccessToTowPro && (
      <TowProCard onClick={handleSelectTowPro} key="towpro-card" />
    ),
    hasAccessToIntlCallback && !isFord && (
      <ToolsIntlCallbackCard key="intl-callback-card" />
    ),
  ].filter(Boolean);

  return tools.length > 0 ? (
    <div className={styles.toolsGrid}>
      <CollaboratorBanner />
      {tools}
    </div>
  ) : (
    <p className={styles.emptyMessage}>No tools available</p>
  );
};

export default ToolsTab;
